/**
 * Voice Transcript Overlay
 *
 * Floating overlay that displays the real-time partial transcript
 * while the user is speaking. Shows above the chat input area.
 *
 * Also displays state indicators (connecting, listening, processing, speaking)
 * and the audio level visualization.
 *
 * Accessibility:
 *   - role="status" + aria-live="polite" for screen reader announcements
 *   - aria-atomic="true" for full content re-read on change
 *   - prefers-reduced-motion: disables animations and transitions
 */

import { cn } from '@/utils'
import { Mic, AudioLines, Radio } from 'lucide-react'
import { Spinner } from '@/components/ui'
import type { VoiceConversationState } from '@/types/voice'

interface VoiceTranscriptOverlayProps {
  /** Current engine state */
  state: VoiceConversationState
  /** Partial transcript text */
  transcript: string
  /** Audio input level 0–1 */
  audioLevel: number
  /** Whether to show the transcript text */
  showTranscript: boolean
  /** Whether open-mic mode is active */
  isOpenMicActive?: boolean
  /** Whether VAD detected active speech */
  isSpeechDetected?: boolean
  className?: string
}

export function VoiceTranscriptOverlay({
  state,
  transcript,
  audioLevel,
  showTranscript,
  isOpenMicActive = false,
  isSpeechDetected = false,
  className,
}: VoiceTranscriptOverlayProps) {
  // Only show overlay during active voice states
  const isVisible =
    state === 'connecting' ||
    state === 'listening' ||
    state === 'processing' ||
    state === 'speaking'

  if (!isVisible) return null

  const getStateDisplay = () => {
    // Open-mic specific states
    if (isOpenMicActive) {
      if (state === 'listening' && isSpeechDetected) {
        return {
          icon: <Mic className="w-4 h-4 text-destructive motion-safe:animate-pulse" />,
          text: 'Recording...',
          textClass: 'text-destructive',
        }
      }
      if (state === 'listening') {
        return {
          icon: <Radio className="w-4 h-4 text-success" />,
          text: 'Open mic — waiting for speech...',
          textClass: 'text-success',
        }
      }
    }

    switch (state) {
      case 'connecting':
        return {
          icon: <Spinner size="sm" aria-hidden />,
          text: 'Connecting to speech recognition...',
          textClass: 'text-warning-strong',
        }
      case 'listening':
        return {
          icon: <Mic className="w-4 h-4 text-destructive motion-safe:animate-pulse" />,
          text: 'Listening...',
          textClass: 'text-destructive',
        }
      case 'processing':
        return {
          icon: <Spinner size="sm" aria-hidden />,
          text: 'Processing...',
          textClass: 'text-info',
        }
      case 'speaking':
        return {
          icon: <AudioLines className="w-4 h-4 text-success" />,
          text: isOpenMicActive ? 'AI speaking — speak to interrupt...' : 'AI is speaking...',
          textClass: 'text-success',
        }
      default:
        return null
    }
  }

  const display = getStateDisplay()
  if (!display) return null

  // Audio level bar width (only during active listening)
  const showLevel = state === 'listening' && (!isOpenMicActive || isSpeechDetected)
  const levelWidth = showLevel ? `${Math.round(audioLevel * 100)}%` : '0%'

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        'absolute bottom-full left-0 right-0 mb-2 mx-4',
        'bg-card border border-border',
        'rounded-xl shadow-lg overflow-hidden',
        'motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200',
        'motion-reduce:animate-none',
        className
      )}
    >
      {/* Audio level indicator bar */}
      {showLevel && (
        <div className="h-0.5 bg-muted">
          <div
            className="h-full bg-destructive motion-safe:transition-all motion-safe:duration-75 motion-reduce:transition-none"
            style={{ width: levelWidth }}
          />
        </div>
      )}

      <div className="px-4 py-3">
        {/* State indicator */}
        <div className="flex items-center gap-2 mb-1">
          {display.icon}
          <span className={cn('text-xs font-medium', display.textClass)}>
            {display.text}
          </span>

          {/* Open-mic badge */}
          {isOpenMicActive && (
            <span className="ml-auto text-[10px] font-medium text-success bg-success/10 px-1.5 py-0.5 rounded-sm">
              Open Mic
            </span>
          )}
        </div>

        {/* Transcript text */}
        {showTranscript && transcript && (
          <p className="text-sm text-foreground leading-relaxed">
            {transcript}
            {state === 'listening' && (
              <span className="inline-block w-0.5 h-4 bg-muted-foreground ml-0.5 motion-safe:animate-pulse align-text-bottom" />
            )}
          </p>
        )}

        {/* Empty state when listening but no transcript yet */}
        {showTranscript && !transcript && state === 'listening' && (!isOpenMicActive || isSpeechDetected) && (
          <p className="text-sm text-muted-foreground italic">
            Start speaking...
          </p>
        )}
      </div>
    </div>
  )
}
